/**
 * PLANIFICACIÓN SEMANAL — cliente HTTP (`/planning/*`).
 * Dominio NUEVO (no es una reetiqueta de los bloques de trabajo / WorkBlock).
 *
 * ADAPTADOR: el backend (`PlanningController`) usa `weekStart`, `plannedStart/plannedEnd/plannedMinutes`
 * y devuelve `plannings/meetings/calls/schedule`; este cliente traduce de/hacia el modelo de la UI.
 *
 * Contrato de tiempos: todos los `startsAt`/`endsAt` son ISO-8601 en UTC
 * (sufijo `Z` o equivalente). El calendario renderiza franjas en UTC para ser
 * determinista; las fechas de día (`date`) son `AAAA-MM-DD`.
 */
import type { AxiosRequestConfig } from 'axios';
import { api } from '../utils/api';

export type PlanningKind = 'TASK' | 'MEETING' | 'CALL' | 'BREAK';
export type PlanningStatus = 'PLANNED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type ConflictCode =
  | 'TASK_TASK_OVERLAP'
  | 'TASK_MEETING_OVERLAP'
  | 'TASK_CALL_OVERLAP'
  | 'OUTSIDE_WORK_SCHEDULE'
  | 'AFTER_DUE_DATE'
  | 'OVER_CAPACITY'
  | string;

export interface PlanningProjectRef {
  id: string;
  name: string;
  clientName?: string | null;
}

export interface PlanningTaskRef {
  id: string;
  taskCode: string;
  title: string;
  project: PlanningProjectRef | null;
  process: string | null;
  priority: string;
  dueDate: string | null;
  /** null → «DURACIÓN PENDIENTE»: la UI nunca inventa una duración. */
  estimatedMinutes: number | null;
}

export interface Planning {
  id: string;
  kind: PlanningKind;
  /** Instante ISO UTC. */
  startsAt: string;
  endsAt: string;
  durationMin: number;
  status: PlanningStatus;
  title: string;
  task: PlanningTaskRef | null;
  note: string | null;
  source: 'MANUAL' | 'NEXUS';
}

export interface WorkScheduleDay {
  date: string;
  /** HH:MM (hora local declarada por el backend) o null si no es día laborable. */
  start: string | null;
  end: string | null;
  isWorkday: boolean;
}

export interface WorkSchedule {
  workStart: string;
  workEnd: string;
  days: WorkScheduleDay[];
}

export interface WeekResponse {
  from: string;
  to: string;
  items: Planning[];
  workSchedule: WorkSchedule;
}

export interface InboxTask {
  id: string;
  taskCode: string;
  title: string;
  project: PlanningProjectRef;
  process: string | null;
  priority: string;
  dueDate: string | null;
  estimatedMinutes: number | null;
  status: string;
}

export interface InboxFilters {
  project?: string;
  process?: string;
  priority?: string;
  dueDate?: string;
}

export interface CapacityDay {
  date: string;
  workMinutes: number;
  meetingMinutes: number;
  callMinutes: number;
  taskMinutes: number;
  availableMinutes: number;
}

export interface CapacitySummary {
  from: string;
  to: string;
  workMinutes: number;
  meetingMinutes: number;
  callMinutes: number;
  taskMinutes: number;
  availableMinutes: number;
  perDay: CapacityDay[];
}

export interface PlanningConflict {
  id: string;
  planningId: string;
  otherPlanningId: string | null;
  code: ConflictCode;
  message: string;
}

export interface CreatePlanningBody {
  taskId: string;
  startsAt: string;
  durationMin: number;
  note?: string;
}

export interface UpdatePlanningBody {
  startsAt?: string;
  durationMin?: number;
  note?: string;
}

/** Propuesta de Nexus (SOLO propuesta: nunca se guarda sola). */
export interface ProposedPlanning {
  key: string;
  taskId: string;
  taskCode: string;
  title: string;
  project: PlanningProjectRef | null;
  process: string | null;
  priority: string;
  dueDate: string | null;
  /** null → «DURACIÓN PENDIENTE» (no se propone franja sin estimación). */
  estimatedMinutes: number | null;
  /** Franja propuesta; null cuando falta la estimación. */
  suggested: { date: string; time: string; durationMin: number } | null;
  reason: string;
}

export interface WeekProposal {
  proposals: ProposedPlanning[];
  notes: string[];
}

// ─── Contrato real del backend (BHM-CLD-REQ-060) ────────────────────────────

interface ApiTaskRef {
  id: string; taskCode: string; title: string; priority: string; dueDate: string | null;
  estimatedMinutes: number | null; project: PlanningProjectRef | null; process: string | null;
}
export interface ApiPlanning {
  id: string; taskId: string; plannedStart: string; plannedEnd: string; plannedMinutes: number;
  status: string; source: string; note: string | null; task: ApiTaskRef | null;
}
export interface ApiMeeting { id: string; title: string; startsAt: string; endsAt: string; status?: string }
export interface ApiCall { id: string; clientName?: string | null; contactName?: string | null; startedAt: string; durationSec: number }
export interface ApiScheduleDay { date: string; working: boolean; startTime: string | null; endTime: string | null }
export interface ApiWeek {
  weekStart: string; from: string; to: string;
  plannings: ApiPlanning[]; meetings: ApiMeeting[]; calls: ApiCall[]; schedule: ApiScheduleDay[];
}

const iso = (d: Date) => d.toISOString();

export function mapPlanning(p: ApiPlanning): Planning {
  return {
    id: p.id,
    kind: 'TASK',
    startsAt: p.plannedStart,
    endsAt: p.plannedEnd,
    durationMin: p.plannedMinutes,
    status: (p.status === 'PLANNED' || p.status === 'IN_PROGRESS' || p.status === 'DONE' || p.status === 'CANCELLED' ? p.status : 'PLANNED') as PlanningStatus,
    title: p.task?.title ?? 'Tarea',
    task: p.task
      ? {
          id: p.task.id, taskCode: p.task.taskCode, title: p.task.title, project: p.task.project,
          process: p.task.process, priority: p.task.priority, dueDate: p.task.dueDate, estimatedMinutes: p.task.estimatedMinutes,
        }
      : null,
    note: p.note,
    source: p.source === 'NEXUS' ? 'NEXUS' : 'MANUAL',
  };
}

export function mapWeek(w: ApiWeek): WeekResponse {
  const meetings: Planning[] = w.meetings.map((m) => ({
    id: m.id, kind: 'MEETING', startsAt: m.startsAt, endsAt: m.endsAt,
    durationMin: Math.max(1, Math.round((Date.parse(m.endsAt) - Date.parse(m.startsAt)) / 60000)),
    status: 'PLANNED', title: m.title, task: null, note: null, source: 'MANUAL',
  }));
  const calls: Planning[] = w.calls.map((c) => {
    const start = new Date(c.startedAt);
    const minutes = Math.max(1, Math.ceil(c.durationSec / 60));
    return {
      id: c.id, kind: 'CALL', startsAt: c.startedAt, endsAt: iso(new Date(start.getTime() + minutes * 60000)),
      durationMin: minutes, status: 'PLANNED', title: c.clientName || c.contactName || 'Llamada', task: null, note: null, source: 'MANUAL',
    };
  });
  const days: WorkScheduleDay[] = w.schedule.map((d) => ({
    date: d.date, start: d.working ? d.startTime : null, end: d.working ? d.endTime : null, isWorkday: d.working && !!d.startTime && !!d.endTime,
  }));
  const starts = days.map((d) => d.start).filter((x): x is string => !!x).sort();
  const ends = days.map((d) => d.end).filter((x): x is string => !!x).sort();
  // Rango visible del calendario (no es un dato de horario): si no hay horario conocido, 08:00–20:00.
  return {
    from: w.from,
    to: w.to,
    items: [...w.plannings.map(mapPlanning), ...meetings, ...calls].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    workSchedule: { workStart: starts[0] ?? '08:00', workEnd: ends[ends.length - 1] ?? '20:00', days },
  };
}

/** Filtros de la UI → query del backend. `process` por nombre se filtra en cliente (el backend usa UUID o PENDING). */
export function inboxQuery(f: InboxFilters): Record<string, string> {
  const q: Record<string, string> = {};
  if (f.project) q.projectId = f.project;
  if (f.priority) q.priority = f.priority;
  if (f.process === 'PENDING') q.process = 'PENDING';
  if (f.dueDate) q.dueBefore = f.dueDate;
  return q;
}

const endOf = (startsAt: string, minutes: number) => new Date(Date.parse(startsAt) + minutes * 60000).toISOString();

export function createPayload(b: CreatePlanningBody) {
  return { taskId: b.taskId, plannedStart: b.startsAt, plannedEnd: endOf(b.startsAt, b.durationMin), plannedMinutes: b.durationMin, ...(b.note !== undefined ? { note: b.note } : {}) };
}

/** Mover/redimensionar. Para cambiar solo la duración hace falta el inicio actual (`startsAt`). */
export function updatePayload(b: UpdatePlanningBody) {
  const out: Record<string, unknown> = {};
  if (b.startsAt !== undefined) out.plannedStart = b.startsAt;
  if (b.startsAt !== undefined && b.durationMin !== undefined) out.plannedEnd = endOf(b.startsAt, b.durationMin);
  if (b.durationMin !== undefined) out.plannedMinutes = b.durationMin;
  if (b.note !== undefined) out.note = b.note;
  return out;
}

export async function apiFetch(url: string, options?: { method?: string; body?: string }): Promise<any> {
  return req({ method: (options?.method || 'GET') as any, url, data: options?.body ? JSON.parse(options.body) : undefined });
}

async function req<T>(config: AxiosRequestConfig): Promise<T> {
  const r = await api.request<T>({
    ...config,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400 || s === 404 || s === 409,
  });
  if (r.status >= 200 && r.status < 300) return r.data;
  const d = r.data && typeof r.data === 'object' ? (r.data as { message?: unknown }) : null;
  const raw = d?.message;
  const msg = Array.isArray(raw) ? raw.join('. ') : typeof raw === 'string' ? raw : 'No se pudo completar la acción';
  throw new PlanningApiError(msg, r.status);
}

export class PlanningApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export const planningApi = {
  week: async (from: string, _to?: string): Promise<WeekResponse> =>
    mapWeek(await req<ApiWeek>({ method: 'GET', url: '/planning/week', params: { weekStart: from } })),
  inbox: async (filters: InboxFilters = {}): Promise<{ tasks: InboxTask[] }> => {
    const r = await req<{ tasks: InboxTask[] }>({ method: 'GET', url: '/planning/inbox', params: inboxQuery(filters) });
    const byName = filters.process && filters.process !== 'PENDING' ? filters.process : null;
    return { tasks: byName ? r.tasks.filter((t) => t.process === byName) : r.tasks };
  },
  create: async (body: CreatePlanningBody): Promise<Planning> =>
    mapPlanning(await req<ApiPlanning>({ method: 'POST', url: '/planning', data: createPayload(body) })),
  update: async (id: string, body: UpdatePlanningBody): Promise<Planning> =>
    mapPlanning(await req<ApiPlanning>({ method: 'PATCH', url: `/planning/${id}`, data: updatePayload(body) })),
  remove: (id: string) => req<{ ok: boolean }>({ method: 'DELETE', url: `/planning/${id}` }),
  replan: async (id: string, body: CreatePlanningBody): Promise<Planning> => {
    const p = createPayload(body);
    return mapPlanning(await req<ApiPlanning>({ method: 'POST', url: `/planning/${id}/replan`, data: { plannedStart: p.plannedStart, plannedEnd: p.plannedEnd, plannedMinutes: p.plannedMinutes } }));
  },
  capacity: (from: string, _to?: string) => req<CapacitySummary>({ method: 'GET', url: '/planning/capacity', params: { weekStart: from } }),
  conflicts: (from: string, _to?: string) => req<{ conflicts: PlanningConflict[] }>({ method: 'GET', url: '/planning/conflicts', params: { weekStart: from } }),
  /**
   * Propuesta de Nexus para la semana. El backend aún no expone este endpoint (404): la UI
   * el backend genera la propuesta — Nexus nunca guarda
   * nada sin confirmación explícita de la persona.
   */
  proposeWeek: async (from: string, _to: string): Promise<WeekProposal> => {
    const raw = await req<any>({ method: 'POST', url: '/work/nexus/organize-week', data: { weekStart: from } });
    // Contrato real backend: { weekStart, days: WeekDayPlan[], projectContexts, confirmedPlannings, respect }
    // Map a WeekProposal: flatten days[].proposals → ProposedPlanning[]
    const proposals: ProposedPlanning[] = [];
    for (const day of raw?.days ?? []) {
      for (const p of day.proposals ?? []) {
        proposals.push({
          key: `${p.taskId}-${day.date}`,
          taskId: p.taskId,
          taskCode: p.taskCode,
          title: p.title,
          project: p.projectId ? { id: p.projectId, name: p.projectName } : null,
          process: p.taskType ?? null,
          priority: p.priority,
          dueDate: p.dueDate ?? null,
          estimatedMinutes: p.estimatedMinutes ?? null,
          suggested: p.proposedStart && p.proposedEnd
            ? { date: day.date, time: p.proposedStart.slice(11, 16), durationMin: Math.round((new Date(p.proposedEnd).getTime() - new Date(p.proposedStart).getTime()) / 60000) }
            : null,
          reason: p.reason ?? '',
        });
      }
    }
    const notes: string[] = [];
    if (raw?.respect?.note) notes.push(raw.respect.note);
    for (const ctx of raw?.projectContexts ?? []) {
      if (ctx.justification) notes.push(`${ctx.projectName}: ${ctx.justification}`);
    }
    return { proposals, notes };
  },
};
